'use client';

import { usePathname } from 'next/navigation';
import { useState } from 'react';

import { useToast } from '@/components/ui/use-toast';
import { getLogger } from '@/logger/logger';
import { addItemsToCart } from '@/store/cart/cart.store';
import { CartItemBody } from '@/types/cart.types';
import { HttpException } from '@/utils/api/api-client';
import { getCartOrigin } from '@/utils/cart/get-cart-origin.util';
import { getDuplicatedCartItemIds } from '@/utils/cart/get-duplicated-cart-items.util';

export const ADD_TO_CART_ERROR_MESSAGE = 'Não foi possível adicionar ao carrinho. Tente novamente.';
export const DUPLICATED_PROCEDURES_MESSAGE = 'Existem procedimentos duplicados no carrinho. Revise antes de prosseguir.';

const UNAVAILABLE_SCHEDULE_SLOT_EXCEPTION = 'UnavailableScheduleSlot';

const logger = getLogger('cart.add-to-cart');

const isUnavailableSlotError = (error: unknown) => error instanceof HttpException
  && error.status === 409
  && (error.data as { name?: unknown } | undefined)?.name === UNAVAILABLE_SCHEDULE_SLOT_EXCEPTION;

export interface AddToCartResult {
  wasAdded: boolean;
  eventInternalId?: string;
}

export interface UseAddToCartOptions {
  onUnavailableSlot?: () => void;
}

export interface UseAddToCartResult {
  addToCart: (items: CartItemBody[]) => Promise<AddToCartResult>;
  isAdding: boolean;
}

export function useAddToCart({ onUnavailableSlot }: UseAddToCartOptions = {}): UseAddToCartResult {
  const pathname = usePathname();
  const { toast } = useToast();
  const [isAdding, setIsAdding] = useState(false);

  async function addToCart(items: CartItemBody[]): Promise<AddToCartResult> {
    setIsAdding(true);

    try {
      const { cart, addedItems, purchaseTrack } = await addItemsToCart(items, getCartOrigin(pathname));

      cart?.warnings?.forEach((warning) => toast.warn(warning));

      const duplicatedItemIds = getDuplicatedCartItemIds(cart?.items ?? []);
      const duplicatedAdditions = addedItems.filter((item) => duplicatedItemIds.has(item.id));

      if (duplicatedAdditions.length > 0) {
        logger.info('An addition left the patient holding the same procedure more than once', {
          cart: {
            event: 'duplicated_procedures',
            duplicated: duplicatedAdditions.map(({ patient, procedure }) => ({
              patient_id: patient.id,
              procedure_id: procedure.id,
            })),
          },
        });
        toast.warn(DUPLICATED_PROCEDURES_MESSAGE);
      }

      return { wasAdded: true, eventInternalId: purchaseTrack?.posthogEventInternalId };
    } catch (error) {
      if (onUnavailableSlot && isUnavailableSlotError(error)) {
        logger.info('The cart refused a slot that is no longer available', {
          cart: {
            event: 'unavailable_slot',
            slot_ids: items.map((item) => item.slotId),
          },
        });
        onUnavailableSlot();

        return { wasAdded: false };
      }

      logger.error('Failed to add items to the cart', {
        cart: {
          event: 'add_failed',
          procedure_ids: items.map((item) => item.procedureId),
          slot_ids: items.map((item) => item.slotId),
        },
        error,
      });
      toast.error(ADD_TO_CART_ERROR_MESSAGE);

      return { wasAdded: false };
    } finally {
      setIsAdding(false);
    }
  }

  return { addToCart, isAdding };
}
